import { QueryClient } from '@tanstack/react-query';

// Create a query client with default options
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      cacheTime: 10 * 60 * 1000,
      // Retry failed requests 2 times
      retry: 2,
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus
      refetchOnWindowFocus: false,
      // Refetch on reconnect
      refetchOnReconnect: true,
      // Refetch on mount only if data is stale
      refetchOnMount: true
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      retryDelay: 1000
    }
  }
});

// Query keys for better cache management
export const queryKeys = {
  products: {
    all: ['products'],
    list: (filters) => ['products', 'list', filters],
    detail: (id) => ['products', 'detail', id],
    search: (query) => ['products', 'search', query],
    category: (category) => ['products', 'category', category]
  },
  orders: {
    all: ['orders'],
    list: ['orders', 'list'],
    detail: (id) => ['orders', 'detail', id]
  },
  user: {
    profile: ['user', 'profile'],
    orders: ['user', 'orders']
  },
  cart: ['cart'],
  wishlist: ['wishlist']
};

// Helper function to invalidate related queries
export const invalidateQueries = {
  products: () => queryClient.invalidateQueries({ queryKey: queryKeys.products.all }),
  orders: () => queryClient.invalidateQueries({ queryKey: queryKeys.orders.all }),
  user: () => queryClient.invalidateQueries({ queryKey: queryKeys.user.profile }),
  cart: () => queryClient.invalidateQueries({ queryKey: queryKeys.cart }),
  wishlist: () => queryClient.invalidateQueries({ queryKey: queryKeys.wishlist })
};

// Prefetch functions for better UX
export const prefetchProduct = async (id) => {
  await queryClient.prefetchQuery({
    queryKey: queryKeys.products.detail(id),
    queryFn: () => fetch(`/api/products/${id}`).then(res => res.json())
  });
};

export const prefetchProducts = async (filters) => {
  await queryClient.prefetchQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: () => fetch('/api/products').then(res => res.json())
  });
};
